import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API } from '../App';
import {
  CheckCircle2,
  AlertTriangle,
  HelpCircle,
  ArrowRight,
  TrendingUp,
  TrendingDown,
  Minus,
} from 'lucide-react';
import { subscribeToBalanceInvalidation } from '../hooks/useAccountBalances';
import { useYear } from '../contexts/YearContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';

/**
 * Clickable "System aligned / needs attention" pill for the Dashboard.
 *
 * - Polls /api/system/health for the one-line status.
 * - On click, opens a drift drawer that pulls /api/settings/balance-comparison
 *   and renders a per-account Expected / Actual / Difference breakdown,
 *   plus a CTA to jump straight to Settings → My Balances.
 *
 * Pure UI/interaction layer on top of the canonical engine. No calc changes.
 */

const DRIFT_TOLERANCE = 0.5; // matches the backend threshold for "aligned"

const fmtMoney = (n) => {
  const v = Number(n || 0);
  const sign = v < 0 ? '-' : '';
  return `${sign}$${Math.abs(v).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const fmtDiff = (d) => {
  const v = Number(d || 0);
  if (v > 0) return `+${fmtMoney(v)}`;
  if (v < 0) return `-${fmtMoney(Math.abs(v))}`;
  return fmtMoney(0);
};

/**
 * Difference semantics (actual − expected):
 *   diff ≈ 0  → aligned (green)
 *   diff < 0  → under-funded vs expected (red)
 *   diff > 0  → over-funded vs expected (amber)
 */
const rowTone = (diff) => {
  const v = Number(diff || 0);
  if (Math.abs(v) <= DRIFT_TOLERANCE) {
    return {
      key: 'aligned',
      label: 'Aligned',
      Icon: Minus,
      badge:
        'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
      value: 'text-emerald-700 dark:text-emerald-300',
    };
  }
  if (v < 0) {
    return {
      key: 'under',
      label: 'Below expected',
      Icon: TrendingDown,
      badge:
        'bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/30',
      value: 'text-rose-700 dark:text-rose-300',
    };
  }
  return {
    key: 'over',
    label: 'Above expected',
    Icon: TrendingUp,
    badge:
      'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30',
    value: 'text-amber-700 dark:text-amber-300',
  };
};

const prettifyName = (key) =>
  key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');

function DriftRow({ name, expected, actual, difference }) {
  const tone = rowTone(difference);
  const { Icon } = tone;
  return (
    <div
      data-testid={`drift-row-${name.toLowerCase().replace(/\s+/g, '-')}`}
      data-tone={tone.key}
      className="grid grid-cols-[1fr_auto] items-center gap-3 py-3 border-b border-gray-200 dark:border-gray-700 last:border-0"
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
            {name}
          </span>
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wide ${tone.badge}`}
          >
            <Icon className="w-3 h-3" aria-hidden="true" />
            {tone.label}
          </span>
        </div>
        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-500 dark:text-gray-400">
          <span>
            Expected{' '}
            <span className="font-mono text-gray-700 dark:text-gray-200">
              {fmtMoney(expected)}
            </span>
          </span>
          <span>
            Actual{' '}
            <span className="font-mono text-gray-700 dark:text-gray-200">
              {fmtMoney(actual)}
            </span>
          </span>
        </div>
      </div>
      <div
        className={`font-mono text-sm font-semibold tabular-nums ${tone.value}`}
      >
        {fmtDiff(difference)}
      </div>
    </div>
  );
}

function SystemHealthPill() {
  const navigate = useNavigate();
  const { selectedYear } = useYear();
  const [state, setState] = useState(null);
  const [open, setOpen] = useState(false);
  const [comparison, setComparison] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState(null);

  const loadHealth = useCallback(async () => {
    try {
      const qs = selectedYear ? `?year=${selectedYear}` : '';
      const res = await axios.get(`${API}/system/health${qs}`);
      setState(res.data);
    } catch {
      setState(null);
    }
  }, [selectedYear]);

  useEffect(() => {
    loadHealth();
    const unsub = subscribeToBalanceInvalidation(loadHealth);
    return () => unsub?.();
  }, [loadHealth]);

  const loadDetail = useCallback(async () => {
    setLoadingDetail(true);
    setDetailError(null);
    try {
      const qs = selectedYear ? `?year=${selectedYear}` : '';
      const res = await axios.get(`${API}/settings/balance-comparison${qs}`);
      setComparison(res.data);
    } catch (err) {
      setDetailError(
        err?.response?.data?.detail || 'Unable to load drift details right now.'
      );
    } finally {
      setLoadingDetail(false);
    }
  }, [selectedYear]);

  const handleOpenChange = (next) => {
    setOpen(next);
    if (next) loadDetail();
  };

  const handleOpenBalances = () => {
    setOpen(false);
    navigate('/settings?tab=balances');
  };

  if (!state) return null;

  const variants = {
    aligned: {
      Icon: CheckCircle2,
      bg: 'bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500/20',
      text: 'text-emerald-700 dark:text-emerald-300',
      dot: 'bg-emerald-400',
      label: 'System aligned',
      focus: 'focus-visible:ring-emerald-500/40',
    },
    needs_attention: {
      Icon: AlertTriangle,
      bg: 'bg-amber-500/10 border-amber-500/30 hover:bg-amber-500/20',
      text: 'text-amber-700 dark:text-amber-300',
      dot: 'bg-amber-400',
      label: 'System needs attention',
      focus: 'focus-visible:ring-amber-500/40',
    },
    unset: {
      Icon: HelpCircle,
      bg: 'bg-gray-500/10 border-gray-400/30 hover:bg-gray-500/20',
      text: 'text-gray-600 dark:text-gray-300',
      dot: 'bg-gray-400',
      label: 'Enter balances to enable',
      focus: 'focus-visible:ring-gray-500/40',
    },
  };
  const v = variants[state.status] || variants.unset;
  const { Icon } = v;

  const cmp = comparison?.comparison;
  const rows = cmp
    ? [
        { name: 'Taxes', ...cmp.taxes },
        { name: 'Personal', ...cmp.personal },
        { name: 'Business', ...cmp.business },
        { name: 'Savings', ...cmp.savings },
        ...(cmp.buffer ? [{ name: 'Buffer', ...cmp.buffer }] : []),
        ...Object.entries(cmp.custom_allocations || {}).map(([k, val]) => ({
          name: prettifyName(k),
          ...val,
        })),
      ]
    : [];

  const drifted = rows.filter(
    (r) => Math.abs(Number(r.difference || 0)) > DRIFT_TOLERANCE
  ).length;
  const isAligned = state.status === 'aligned';
  const isUnset = state.status === 'unset';

  const title = isAligned
    ? 'System aligned'
    : isUnset
    ? 'Balances not entered yet'
    : 'System drift detected';
  const subtitle = isAligned
    ? 'Your actual balances match expected balances based on your income and expenses.'
    : isUnset
    ? 'Enter your actual bank balances in Settings → My Balances to enable reconciliation.'
    : "Your actual balances don't match expected balances based on your income and expenses.";

  return (
    <>
      <button
        type="button"
        data-testid="system-health-pill"
        data-health-status={state.status}
        aria-label={`${v.label}. Click for details.`}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={state.reason}
        onClick={() => handleOpenChange(true)}
        className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-medium transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent ${v.bg} ${v.text} ${v.focus}`}
      >
        <span
          className={`inline-block w-2 h-2 rounded-full ${v.dot}`}
          aria-hidden="true"
        />
        <Icon className="w-3 h-3" aria-hidden="true" />
        <span>{v.label}</span>
      </button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          data-testid="system-health-drawer"
          className="max-w-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700"
        >
          <DialogHeader>
            <DialogTitle
              data-testid="system-health-drawer-title"
              className="text-gray-900 dark:text-gray-100 text-lg"
            >
              {title}
            </DialogTitle>
            <DialogDescription className="text-gray-600 dark:text-gray-400">
              {subtitle}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-2">
            {loadingDetail && (
              <div
                data-testid="system-health-drawer-loading"
                className="py-8 text-center text-sm text-gray-500 dark:text-gray-400"
              >
                Loading drift details…
              </div>
            )}

            {!loadingDetail && detailError && (
              <div
                data-testid="system-health-drawer-error"
                className="py-4 px-3 rounded-md bg-rose-500/10 border border-rose-500/30 text-sm text-rose-700 dark:text-rose-300"
              >
                {detailError}
              </div>
            )}

            {!loadingDetail && !detailError && rows.length > 0 && (
              <div
                data-testid="system-health-drawer-rows"
                className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/40 px-4"
              >
                {rows.map((r) => (
                  <DriftRow key={r.name} {...r} />
                ))}
              </div>
            )}

            {!loadingDetail && !detailError && rows.length === 0 && (
              <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                No account data to compare yet.
              </div>
            )}

            {!isAligned && !isUnset && (
              <p
                data-testid="system-health-drawer-helper"
                className="mt-4 text-xs text-gray-500 dark:text-gray-400 leading-relaxed"
              >
                Update your balances to match your real accounts and bring your
                system back into alignment.
                {drifted > 0 && (
                  <>
                    {' '}
                    <span className="font-medium text-gray-700 dark:text-gray-300">
                      {drifted} account{drifted === 1 ? '' : 's'}
                    </span>{' '}
                    currently off target.
                  </>
                )}
              </p>
            )}
          </div>

          <div className="mt-4 flex flex-col-reverse sm:flex-row sm:justify-end sm:items-center gap-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="system-health-drawer-dismiss"
              className="px-4 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              Close
            </button>
            <button
              type="button"
              onClick={handleOpenBalances}
              data-testid="system-health-drawer-open-balances"
              className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md bg-blue-600 hover:bg-blue-700 text-white shadow-sm transition-colors"
            >
              Open My Balances
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default SystemHealthPill;
